import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import ProfileCompletionCard from './components/ProfileCompletionCard';
import NotificationPanel from './components/NotificationPanel';
import ProfilePreview from './components/ProfilePreview';
import AntiBiasToggle from './components/AntiBiasToggle';
import QuickActions from './components/QuickActions';
import StatisticsSection from './components/StatisticsSection';

const JobSeekerDashboard = () => {
  const [isAntiBiasMode, setIsAntiBiasMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('workfolio_language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Mock data for dashboard
  const profileData = {
    candidateId: "1234",
    title: "Senior Frontend Developer",
    location: "Oslo, Norway",
    experience: "5+ years",
    age: 28,
    availability: "Available",
    salaryRange: "650,000 - 800,000",
    education: "Master's in Computer Science, University of Oslo",
    skills: [
      { name: "React", level: "Expert" },
      { name: "TypeScript", level: "Advanced" },
      { name: "Node.js", level: "Intermediate" },
      { name: "Python", level: "Intermediate" }
    ],
    languages: [
      { language: "Norwegian", level: "Native" },
      { language: "English", level: "Fluent" },
      { language: "German", level: "Basic" }
    ]
  };

  const notifications = [
    {
      id: 1,
      type: "unlock",
      title: "Profile Unlocked",
      message: "TechCorp AS unlocked your profile and wants to connect",
      timestamp: new Date(Date.now() - 1800000),
      read: false
    },
    {
      id: 2,
      type: "view",
      title: "Profile Viewed",
      message: "Your profile was viewed by 3 employers today",
      timestamp: new Date(Date.now() - 3600000),
      read: false
    },
    {
      id: 3,
      type: "message",
      title: "New Message",
      message: "StartupHub sent you a message about a Senior Developer position",
      timestamp: new Date(Date.now() - 7200000),
      read: true
    },
    {
      id: 4,
      type: "match",
      title: "New Match",
      message: "Your skills match 95% with a new job posting",
      timestamp: new Date(Date.now() - 14400000),
      read: true
    }
  ];

  const statistics = {
    "7d": {
      profileViews: 24,
      searchAppearances: 18,
      employerInterest: 7,
      profileUnlocks: 3
    },
    "30d": {
      profileViews: 89,
      searchAppearances: 67,
      employerInterest: 23,
      profileUnlocks: 8
    },
    "90d": {
      profileViews: 234,
      searchAppearances: 189,
      employerInterest: 56,
      profileUnlocks: 19
    },
    topSkills: [
      { name: "React", percentage: 85 },
      { name: "TypeScript", percentage: 72 },
      { name: "Node.js", percentage: 58 },
      { name: "Python", percentage: 43 }
    ],
    industryInterest: [
      { name: "Technology", count: 12 },
      { name: "Fintech", count: 8 },
      { name: "E-commerce", count: 5 },
      { name: "Healthcare", count: 3 }
    ]
  };

  const handleEditProfile = () => {
    // Navigate to profile edit page
    console.log('Edit profile clicked');
  };

  const handleUpdateAvailability = () => {
    // Open availability update modal
    console.log('Update availability clicked');
  };

  const handleViewSettings = () => {
    // Navigate to settings page
    console.log('View settings clicked');
  };

  const handleAntiBiasToggle = () => {
    setIsAntiBiasMode(!isAntiBiasMode);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Welcome back, Anonymous Candidate #{profileData?.candidateId}
              </h1>
              <p className="text-muted-foreground">
                Manage your profile and track your job search progress
              </p>
            </div>
            <div className="hidden md:flex items-center space-x-3">
              <Button variant="outline" iconName="Download">
                Export Profile
              </Button>
              <Button iconName="Edit3">
                Edit Profile
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Profile Views"
            value="24"
            subtitle="Last 7 days"
            icon="Eye"
            trend={{ type: 'up', value: '+12%' }}
            color="primary"
          />
          <MetricsCard
            title="Employer Unlocks"
            value="3"
            subtitle="This week"
            icon="Unlock"
            trend={{ type: 'up', value: '+2' }}
            color="success"
          />
          <MetricsCard
            title="Search Appearances"
            value="18"
            subtitle="Last 7 days"
            icon="Search"
            trend={{ type: 'neutral', value: 'Stable' }}
            color="accent"
          />
          <ProfileCompletionCard
            completionScore={85}
            missingFields={["Portfolio links", "Certifications"]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Preview */}
            <ProfilePreview 
              profile={profileData} 
              isAntiBiasMode={isAntiBiasMode}
            />

            {/* Statistics Section */}
            <StatisticsSection statistics={statistics} />

            {/* Quick Actions */}
            <QuickActions
              onEditProfile={handleEditProfile}
              onUpdateAvailability={handleUpdateAvailability}
              onViewSettings={handleViewSettings}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Notifications */}
            <NotificationPanel notifications={notifications} />

            {/* Anti-Bias Toggle */}
            <AntiBiasToggle
              isEnabled={isAntiBiasMode}
              onToggle={handleAntiBiasToggle}
            />

            {/* Profile Tips */}
            <div className="bg-card rounded-xl p-6 shadow-elevation border border-border">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="Lightbulb" size={20} className="text-warning" />
                <h3 className="text-lg font-semibold text-foreground">Profile Tips</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Add portfolio links to increase your profile completion score
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Update your availability status to get more relevant matches
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Enable anti-bias mode to promote fair hiring practices
                  </p>
                </div>
              </div>
            </div>

            {/* Support Card */}
            <div className="bg-card rounded-xl p-6 shadow-elevation border border-border">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="HelpCircle" size={20} className="text-accent" />
                <h3 className="text-lg font-semibold text-foreground">Need Help?</h3>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Get support with your profile or have questions about the platform
              </p>
              
              <div className="space-y-2">
                <Button variant="outline" size="sm" fullWidth asChild>
                  <Link to="/contact">Contact Support</Link>
                </Button>
                <Button variant="ghost" size="sm" fullWidth>
                  View FAQ
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Actions */}
        <div className="md:hidden fixed bottom-6 right-6 space-y-3">
          <Button size="icon" className="h-12 w-12 rounded-full shadow-elevation-lg">
            <Icon name="Edit3" size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerDashboard;